import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { clamp, isNil } from 'es-toolkit';

export const ACCOUNT_ROW_LIFT_HOLD_MS = 300;
export const ACCOUNT_ROW_DRAG_ID_ATTRIBUTE = 'data-account-row-id';

const LIFT_CANCEL_MOVE_PX = 8;
const EDGE_SCROLL_ZONE_PX = 72;
const EDGE_SCROLL_STEP_PX = 14;
const LIFT_SCALE = 1.015;
const LIFT_IN_MS = 140;
const SETTLE_MS = 200;
const SETTLE_EASING = 'cubic-bezier(0.2, 0, 0, 1)';
const ROW_SELECTOR = `[${ACCOUNT_ROW_DRAG_ID_ATTRIBUTE}]`;

type RowSnapshot = {
  readonly rowId: string;
  readonly top: number;
  readonly height: number;
};

type DragState = {
  readonly rowId: string;
  readonly settling: boolean;
  readonly snapshot: readonly RowSnapshot[];
};

type PendingLift = {
  readonly rowId: string;
  readonly startClientX: number;
  readonly startClientY: number;
  readonly node: HTMLElement;
};

type LiftedGeometry = {
  readonly rowId: string;
  readonly startLayoutY: number;
  readonly startScrollTop: number;
  readonly minOffsetY: number;
  readonly maxOffsetY: number;
};

export type AccountRowDrag = {
  readonly listRef: RefObject<HTMLDivElement | null>;
  readonly liftedId: string | null;
  readonly settling: boolean;
  readonly suppressTransitions: boolean;
  readonly displacementOf: (rowId: string) => number;
  readonly onRowPointerDown: (rowId: string, event: ReactPointerEvent<HTMLElement>) => void;
  readonly onRowPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onRowPointerUp: () => void;
  readonly onRowClickCapture: (event: ReactMouseEvent<HTMLElement>) => void;
  readonly onRowContextMenu: (event: ReactMouseEvent<HTMLElement>) => void;
  readonly onHandlePointerDown: (rowId: string, event: ReactPointerEvent<HTMLElement>) => void;
};

function snapshotOf(list: HTMLElement): readonly RowSnapshot[] {
  return [...list.querySelectorAll<HTMLElement>(ROW_SELECTOR)].flatMap((node) => {
    const rowId = node.getAttribute(ACCOUNT_ROW_DRAG_ID_ATTRIBUTE);
    if (isNil(rowId)) {
      return [];
    }
    const rect = node.getBoundingClientRect();
    return [{ rowId, top: rect.top, height: rect.height }];
  });
}

function indexIn(snapshot: readonly RowSnapshot[], rowId: string | null) {
  return isNil(rowId) ? -1 : snapshot.findIndex((row) => row.rowId === rowId);
}

function rowAtLayoutY(snapshot: readonly RowSnapshot[], layoutY: number) {
  const inside = snapshot.findIndex((row) => layoutY >= row.top && layoutY <= row.top + row.height);
  if (inside !== -1) {
    return inside;
  }
  const first = snapshot.at(0);
  const last = snapshot.at(-1);
  if (!isNil(first) && layoutY < first.top) {
    return 0;
  }
  if (!isNil(last) && layoutY > last.top + last.height) {
    return snapshot.length - 1;
  }
  return -1;
}

function scrollParentOf(node: HTMLElement) {
  let current = node.parentElement;
  while (!isNil(current)) {
    const { overflowY } = window.getComputedStyle(current);
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

function scrollTopOf(scroller: HTMLElement | null) {
  return isNil(scroller) ? window.scrollY : scroller.scrollTop;
}

function verticalBoundsOf(scroller: HTMLElement | null) {
  if (isNil(scroller)) {
    return { top: 0, bottom: window.innerHeight };
  }
  const rect = scroller.getBoundingClientRect();
  return { top: rect.top, bottom: rect.bottom };
}

function scrollVerticallyBy(scroller: HTMLElement | null, delta: number) {
  if (isNil(scroller)) {
    window.scrollBy(0, delta);
    return;
  }
  scroller.scrollTop += delta;
}

function startsOnControl(target: EventTarget | null) {
  return target instanceof Element && !isNil(target.closest('button'));
}

function startsInsideRow(target: EventTarget | null, row: HTMLElement) {
  return target instanceof Node && row.contains(target);
}

function restingOffset(snapshot: readonly RowSnapshot[], liftedIndex: number, targetIndex: number) {
  const lifted = snapshot.at(liftedIndex);
  const target = snapshot.at(targetIndex);
  if (isNil(lifted) || isNil(target)) {
    return 0;
  }
  return targetIndex > liftedIndex
    ? target.top + target.height - (lifted.top + lifted.height)
    : target.top - lifted.top;
}

export function useAccountRowDrag(
  enabled: boolean,
  onReorder: (movedId: string, targetId: string) => void,
): AccountRowDrag {
  const listRef = useRef<HTMLDivElement | null>(null);
  const holdRef = useRef<number | null>(null);
  const settleRef = useRef<number | null>(null);
  const teardownRef = useRef<(() => void) | null>(null);
  const pendingRef = useRef<PendingLift | null>(null);
  const geometryRef = useRef<LiftedGeometry | null>(null);
  const liftedNodeRef = useRef<HTMLElement | null>(null);
  const snapshotRef = useRef<readonly RowSnapshot[]>([]);
  const targetRef = useRef<string | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const pointerYRef = useRef(0);
  const movedWhileLiftedRef = useRef(false);
  const draggedRef = useRef(false);
  const reorderRef = useRef(onReorder);

  const [drag, setDrag] = useState<DragState | null>(null);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [suppressTransitions, setSuppressTransitions] = useState(false);

  reorderRef.current = onReorder;

  function clearHold() {
    if (!isNil(holdRef.current)) {
      window.clearTimeout(holdRef.current);
      holdRef.current = null;
    }
    pendingRef.current = null;
  }

  function paint(offsetY: number, scale: number) {
    const node = liftedNodeRef.current;
    if (isNil(node)) {
      return;
    }
    node.style.transform = `translateY(${offsetY}px) scale(${scale})`;
  }

  function teardownGesture() {
    const teardown = teardownRef.current;
    teardownRef.current = null;
    if (!isNil(teardown)) {
      teardown();
    }
  }

  function layoutYOf(clientY: number) {
    const geometry = geometryRef.current;
    if (isNil(geometry)) {
      return clientY;
    }
    return clientY + (scrollTopOf(scrollerRef.current) - geometry.startScrollTop);
  }

  function edgeScrollDelta() {
    if (!movedWhileLiftedRef.current) {
      return 0;
    }
    const bounds = verticalBoundsOf(scrollerRef.current);
    const clientY = pointerYRef.current;
    if (clientY < bounds.top + EDGE_SCROLL_ZONE_PX) {
      return -EDGE_SCROLL_STEP_PX;
    }
    if (clientY > bounds.bottom - EDGE_SCROLL_ZONE_PX) {
      return EDGE_SCROLL_STEP_PX;
    }
    return 0;
  }

  function syncToPointer() {
    const geometry = geometryRef.current;
    const snapshot = snapshotRef.current;
    if (isNil(geometry)) {
      return;
    }
    const layoutY = layoutYOf(pointerYRef.current);
    paint(clamp(layoutY - geometry.startLayoutY, geometry.minOffsetY, geometry.maxOffsetY), LIFT_SCALE);

    const hovered = rowAtLayoutY(snapshot, layoutY);
    const landed = hovered === -1 || hovered === indexIn(snapshot, geometry.rowId) ? null : snapshot[hovered];
    const landedId = landed?.rowId ?? null;
    if (landedId !== targetRef.current) {
      targetRef.current = landedId;
      setTargetId(landedId);
    }
  }

  function commit() {
    const node = liftedNodeRef.current;
    const movedId = geometryRef.current?.rowId ?? null;
    const landedOn = targetRef.current;

    teardownGesture();
    if (!isNil(node)) {
      node.style.transform = '';
      node.style.transition = '';
    }
    if (!isNil(settleRef.current)) {
      window.clearTimeout(settleRef.current);
      settleRef.current = null;
    }
    liftedNodeRef.current = null;
    geometryRef.current = null;
    targetRef.current = null;
    scrollerRef.current = null;
    snapshotRef.current = [];

    setSuppressTransitions(true);
    if (!isNil(movedId) && !isNil(landedOn)) {
      reorderRef.current(movedId, landedOn);
    }
    setDrag(null);
    setTargetId(null);
    window.requestAnimationFrame(() => {
      setSuppressTransitions(false);
    });
  }

  function settle() {
    const node = liftedNodeRef.current;
    const geometry = geometryRef.current;
    const snapshot = snapshotRef.current;

    teardownGesture();
    if (isNil(node) || isNil(geometry)) {
      commit();
      return;
    }
    const liftedIndex = indexIn(snapshot, geometry.rowId);
    const targetIndex = indexIn(snapshot, targetRef.current);
    const offsetY = targetIndex === -1 ? 0 : restingOffset(snapshot, liftedIndex, targetIndex);

    node.style.transition = `transform ${SETTLE_MS}ms ${SETTLE_EASING}`;
    paint(offsetY, 1);
    setDrag((current) => (isNil(current) ? current : { ...current, settling: true }));
    settleRef.current = window.setTimeout(commit, SETTLE_MS);
  }

  function abandon() {
    targetRef.current = null;
    setTargetId(null);
    settle();
  }

  function onWindowPointerMove(event: PointerEvent) {
    const node = liftedNodeRef.current;
    if (!isNil(node) && !movedWhileLiftedRef.current) {
      node.style.transition = 'none';
    }
    movedWhileLiftedRef.current = true;
    pointerYRef.current = event.clientY;
    syncToPointer();
  }

  function lift(pending: PendingLift, clientY: number) {
    const list = listRef.current;
    if (isNil(list)) {
      return;
    }
    const snapshot = snapshotOf(list);
    const liftedIndex = indexIn(snapshot, pending.rowId);
    const lifted = snapshot.at(liftedIndex);
    const first = snapshot.at(0);
    const last = snapshot.at(-1);
    if (isNil(lifted) || isNil(first) || isNil(last)) {
      return;
    }
    const scroller = scrollParentOf(pending.node);

    clearHold();
    teardownGesture();
    snapshotRef.current = snapshot;
    liftedNodeRef.current = pending.node;
    scrollerRef.current = scroller;
    targetRef.current = null;
    geometryRef.current = {
      rowId: pending.rowId,
      startLayoutY: clientY,
      startScrollTop: scrollTopOf(scroller),
      minOffsetY: first.top - lifted.top,
      maxOffsetY: last.top + last.height - (lifted.top + lifted.height),
    };
    pointerYRef.current = clientY;
    movedWhileLiftedRef.current = false;
    draggedRef.current = true;

    pending.node.style.transition = `transform ${LIFT_IN_MS}ms ease-out`;
    paint(0, LIFT_SCALE);

    function blockTouchScroll(event: TouchEvent) {
      event.preventDefault();
    }

    let frame = window.requestAnimationFrame(function step() {
      const delta = edgeScrollDelta();
      if (delta !== 0) {
        scrollVerticallyBy(scrollerRef.current, delta);
        syncToPointer();
      }
      frame = window.requestAnimationFrame(step);
    });

    window.addEventListener('pointermove', onWindowPointerMove);
    window.addEventListener('pointerup', settle);
    window.addEventListener('pointercancel', abandon);
    document.addEventListener('touchmove', blockTouchScroll, { passive: false });
    teardownRef.current = () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onWindowPointerMove);
      window.removeEventListener('pointerup', settle);
      window.removeEventListener('pointercancel', abandon);
      document.removeEventListener('touchmove', blockTouchScroll);
    };

    setDrag({ rowId: pending.rowId, settling: false, snapshot });
    setTargetId(null);
  }

  useEffect(
    () => () => {
      clearHold();
      teardownGesture();
      if (!isNil(settleRef.current)) {
        window.clearTimeout(settleRef.current);
      }
    },
    [],
  );

  return {
    listRef,
    liftedId: drag?.rowId ?? null,
    settling: drag?.settling ?? false,
    suppressTransitions,
    displacementOf: (rowId) => {
      if (isNil(drag) || isNil(targetId)) {
        return 0;
      }
      const liftedIndex = indexIn(drag.snapshot, drag.rowId);
      const targetIndex = indexIn(drag.snapshot, targetId);
      const index = indexIn(drag.snapshot, rowId);
      const height = drag.snapshot.at(liftedIndex)?.height ?? 0;
      if (index === -1 || index === liftedIndex || targetIndex === -1) {
        return 0;
      }
      if (targetIndex > liftedIndex && index > liftedIndex && index <= targetIndex) {
        return -height;
      }
      if (targetIndex < liftedIndex && index >= targetIndex && index < liftedIndex) {
        return height;
      }
      return 0;
    },
    onRowPointerDown: (rowId, event) => {
      draggedRef.current = false;
      if (!enabled || !event.isPrimary || event.button !== 0 || startsOnControl(event.target)) {
        return;
      }
      if (!startsInsideRow(event.target, event.currentTarget)) {
        return;
      }
      if (!isNil(drag)) {
        return;
      }
      const pending: PendingLift = {
        rowId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        node: event.currentTarget,
      };
      pendingRef.current = pending;
      holdRef.current = window.setTimeout(() => {
        holdRef.current = null;
        lift(pending, pending.startClientY);
      }, ACCOUNT_ROW_LIFT_HOLD_MS);
    },
    onRowPointerMove: (event) => {
      const pending = pendingRef.current;
      if (isNil(pending)) {
        return;
      }
      const travelled = Math.abs(event.clientY - pending.startClientY) + Math.abs(event.clientX - pending.startClientX);
      if (travelled > LIFT_CANCEL_MOVE_PX) {
        clearHold();
      }
    },
    onRowPointerUp: clearHold,
    onRowClickCapture: (event) => {
      if (!draggedRef.current) {
        return;
      }
      draggedRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
    onRowContextMenu: (event) => {
      if (!isNil(drag) || !isNil(pendingRef.current)) {
        event.preventDefault();
      }
    },
    onHandlePointerDown: (rowId, event) => {
      draggedRef.current = false;
      if (!enabled || !event.isPrimary || event.button !== 0) {
        return;
      }
      const rowNode = event.currentTarget.closest(ROW_SELECTOR);
      if (!(rowNode instanceof HTMLElement)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      lift({ rowId, startClientX: event.clientX, startClientY: event.clientY, node: rowNode }, event.clientY);
    },
  };
}
